import React from "react";

/**
 * Renders PayrollAction's structured payslip data as a styled card.
 *
 * Expects payslip shaped like:
 * {
 *   employee: { name, employee_id, designation, department },
 *   period: { title, from_date, to_date } | null,
 *   base_salary: { monthly_salary, payroll_type, currency } | null,
 *   allowances: [ { allowance_code, amount, frequency } ],
 *   line_items: [ { payroll_header, description, amount } ],
 *   net_pay: number,
 *   payment_status: { amount_to_be_paid, status, is_paid, paid_date } | null
 * }
 */
export default function PayslipCard({ payslip }) {
  if (!payslip) return null;

  const { employee, period, base_salary, allowances = [], line_items = [], net_pay, payment_status } = payslip;

  const currency = base_salary?.currency || "";

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <div>
          <div style={styles.name}>{employee?.name}</div>
          <div style={styles.muted}>
            {employee?.employee_id}
            {employee?.designation ? ` · ${employee.designation}` : ""}
            {employee?.department ? ` · ${employee.department}` : ""}
          </div>
        </div>
        {period && (
          <div style={styles.period}>
            {period.title || `${period.from_date} – ${period.to_date}`}
          </div>
        )}
      </div>

      {base_salary?.monthly_salary && (
        <div style={styles.row}>
          <span>Base Salary ({base_salary.payroll_type || "monthly"})</span>
          <span style={styles.amount}>{currency} {formatAmount(base_salary.monthly_salary)}</span>
        </div>
      )}

      {allowances.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Allowances</div>
          {allowances.map((a, i) => (
            <div style={styles.row} key={i}>
              <span>{a.allowance_code}</span>
              <span style={styles.amount}>{currency} {formatAmount(a.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {line_items.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Latest Payroll Line Items</div>
          {line_items.map((item, i) => (
            <div style={styles.row} key={i}>
              <span>{item.payroll_header || item.description}</span>
              <span style={styles.amount}>{currency} {formatAmount(item.amount)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.netPayRow}>
        <span>Net Pay {period ? "(latest period)" : "(estimated)"}</span>
        <span style={styles.netPayAmount}>{currency} {formatAmount(net_pay)}</span>
      </div>

      {payment_status && (
        <div style={styles.statusRow}>
          Status: <strong>{payment_status.is_paid ? "Paid" : payment_status.status || "Pending"}</strong>
          {payment_status.paid_date ? ` · ${payment_status.paid_date}` : ""}
        </div>
      )}

      <div style={styles.disclaimer}>
        Figures pulled directly from payroll records. Verify against the Payroll module before acting on this.
      </div>
    </div>
  );
}

function formatAmount(value) {
  const num = Number(value);
  return isNaN(num) ? value : num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const styles = {
  wrapper: {
    maxWidth: 480,
    margin: "8px 0",
    background: "#fff",
    border: "1px solid #e4e7ec",
    borderRadius: 10,
    padding: "20px 22px",
    boxShadow: "0 1px 3px rgba(16,24,40,0.08)",
    fontFamily: "system-ui, -apple-system, sans-serif",
    color: "#1d2939",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    borderBottom: "1px solid #eaecf0",
    paddingBottom: 12,
    marginBottom: 14,
  },
  name: { fontSize: 16, fontWeight: 700 },
  muted: { fontSize: 12.5, color: "#667085", marginTop: 2 },
  period: { fontSize: 12.5, color: "#2563eb", fontWeight: 600, whiteSpace: "nowrap" },
  row: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 14,
    padding: "6px 0",
  },
  section: { marginTop: 10 },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: "uppercase",
    color: "#667085",
    letterSpacing: 0.4,
    marginBottom: 4,
  },
  amount: { fontVariantNumeric: "tabular-nums" },
  netPayRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 14,
    paddingTop: 12,
    borderTop: "2px solid #2563eb",
    fontSize: 15,
    fontWeight: 700,
  },
  netPayAmount: { color: "#2563eb" },
  statusRow: { marginTop: 10, fontSize: 13, color: "#475467" },
  disclaimer: {
    marginTop: 14,
    fontSize: 11,
    color: "#98a2b3",
    fontStyle: "italic",
  },
};
